import { areDisplayCreativesComplete } from '@features/campaigns/shared/config/adProductsCountryConfig.helpers.ts';
import {
  CampaignAdProductsCountryConfig,
  AdProductsReviewNotEnoughCreativesInfoTranslationKey,
  AdProductsReviewUploadedCreativesLabelKey,
} from '@features/campaigns/shared/config/adProductsCountryConfig.ts';
import { AdProductsData } from '@features/campaigns/shared/containers/Review/Review.dto';
import {
  CampaignMediaType,
  CampaignMediaTypes,
  CampaignStatus,
  CampaignStatuses,
  CampaignType,
  CampaignTypes,
} from '@features/campaigns/shared/types/campaign.ts';
import { AccessRight, AccessRights } from '@shared/api/commonApiInterfaces.ts';

export interface AdProductsReviewSummaryVisibility {
  shouldShowVideoStatus: boolean;
  shouldShowInternalCreativeFields: boolean;
  shouldShowVideoProductsIncluded: boolean;
  shouldShowTrackingUrl: boolean;
}

interface AdProductsReviewVideoCreativesVisibility {
  shouldShow: boolean;
}

export interface AdProductsReviewDisplayCreativesVisibility {
  shouldShow: boolean;
  isImageUploaded: boolean;
  isSwitchInCreativeAvailable: boolean;
  shouldShowUploadReminder: boolean;
  uploadedCreativesLabelKey: AdProductsReviewUploadedCreativesLabelKey;
  notEnoughCreativesInfoTranslationKey: AdProductsReviewNotEnoughCreativesInfoTranslationKey;
}

export interface AdProductsReviewVisibility {
  summary: AdProductsReviewSummaryVisibility;
  videoCreatives: AdProductsReviewVideoCreativesVisibility;
  displayCreatives: AdProductsReviewDisplayCreativesVisibility;
}

export interface GetAdProductsReviewVisibilityParams {
  campaignType?: CampaignType;
  campaignStatus?: CampaignStatus;
  mediaType?: CampaignMediaType;
  data: AdProductsData;
  accessRights?: AccessRight[];
  isCurrentUserExternal?: boolean;
  countryConfig: CampaignAdProductsCountryConfig;
}

export function getAdProductsReviewVisibility({
  campaignType,
  campaignStatus,
  mediaType,
  data,
  accessRights,
  isCurrentUserExternal,
  countryConfig,
}: GetAdProductsReviewVisibilityParams): AdProductsReviewVisibility {
  const isSwitchInImageUploaded = Boolean(data.switchInLeftImageUrl || data.switchInBottomImageUrl);
  const isLegacyImageUploaded = Boolean(data.landingImageUrl || data.lShapeImageUrl);
  const isImageUploaded = countryConfig.isSwitchInCreativeAvailable ? isSwitchInImageUploaded : isLegacyImageUploaded;
  const isCrossMediaType = mediaType === CampaignMediaTypes.CROSS;
  const isAdminUser = Boolean(accessRights?.includes(AccessRights.EDIT_ALL));
  const isCampaignInternal = campaignType === CampaignTypes.INTERNAL;
  const isCampaignSelfService = campaignType === CampaignTypes.SELF_SERVICE;
  const isVideoAdProductSelected = data.isVideoAdProductSelected;
  const isDisplayAdProductSelected = data.isDisplayAdProductSelected;
  const isAllImageCreativesUploaded = areDisplayCreativesComplete(countryConfig, {
    lShapeImageUrl: data.lShapeImageUrl,
    landingImageUrl: data.landingImageUrl,
    switchInLeftImageUrl: data.switchInLeftImageUrl,
    switchInBottomImageUrl: data.switchInBottomImageUrl,
  });
  const isAllVideoCreativesUploaded = Boolean(data.videoSpotUrl);
  const shouldShowSelfServiceVideoFields = isCampaignSelfService && isVideoAdProductSelected;

  const shouldShowUploadReminder =
    ((isVideoAdProductSelected && !isAllVideoCreativesUploaded) ||
      (!isAllImageCreativesUploaded && isDisplayAdProductSelected)) &&
    campaignStatus === CampaignStatuses.DRAFT &&
    campaignType === CampaignTypes.SELF_SERVICE &&
    !isCrossMediaType &&
    (isCurrentUserExternal || isAdminUser);

  return {
    summary: {
      shouldShowVideoStatus: shouldShowSelfServiceVideoFields,
      shouldShowInternalCreativeFields: isCampaignInternal && !isCrossMediaType,
      shouldShowVideoProductsIncluded: isCampaignInternal,
      shouldShowTrackingUrl: shouldShowSelfServiceVideoFields,
    },
    videoCreatives: {
      shouldShow: shouldShowSelfServiceVideoFields,
    },
    displayCreatives: {
      shouldShow: isCampaignSelfService && !isCrossMediaType,
      isImageUploaded,
      isSwitchInCreativeAvailable: countryConfig.isSwitchInCreativeAvailable,
      shouldShowUploadReminder,
      uploadedCreativesLabelKey: countryConfig.reviewUploadedCreativesLabelKey,
      notEnoughCreativesInfoTranslationKey: countryConfig.reviewNotEnoughCreativesInfoTranslationKey,
    },
  };
}
